import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { Zap, Gauge, Landmark, TrendingUp, Loader2 } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { RateioUnidadeGeradora } from '../../../types';
import { buscarHistoricoConsumoUcs, gerarUltimosMeses, normalizarMesReferencia } from './consumoHistorico';
import RegistrarGeracaoModal from './RegistrarGeracaoModal';

const round2 = (n: number) => Math.round(n * 100) / 100;
const formatKwh = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const KpiCard: React.FC<{ icon: React.ReactNode; label: string; value: string }> = ({ icon, label, value }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
    <div className="flex items-center gap-2 mb-2 text-gray-500 dark:text-gray-400">
      {icon}
      <p className="text-[11px] font-bold uppercase tracking-wider">{label}</p>
    </div>
    <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
  </div>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-6">
    <h3 className="font-bold text-gray-800 dark:text-white mb-4">{title}</h3>
    <div className="h-64 w-full">{children}</div>
  </div>
);

type PorUgRow = { ug: RateioUnidadeGeradora; numUcs: number; energiaDisponivelKwh: number | null; percentDisponivel: number | null };

const DashboardRateioView: React.FC = () => {
  const meses12 = useMemo(() => gerarUltimosMeses(12), []);
  const [mesSelecionado, setMesSelecionado] = useState(meses12[meses12.length - 1]);
  const [loading, setLoading] = useState(true);
  const [ugs, setUgs] = useState<RateioUnidadeGeradora[]>([]);
  const [kpis, setKpis] = useState({
    geracaoTotalKwh: 0,
    consumoAtendidoKwh: 0,
    coberturaPercent: null as number | null,
    saldoAncoraTotalKwh: 0,
  });
  const [porUg, setPorUg] = useState<PorUgRow[]>([]);
  const [evolucaoData, setEvolucaoData] = useState<{ mes: string; Consumo: number; Geração: number }[]>([]);
  const [sobraDeficitData, setSobraDeficitData] = useState<{ ug: string; Sobra: number; Déficit: number }[]>([]);
  const [showRegistrarGeracao, setShowRegistrarGeracao] = useState<RateioUnidadeGeradora | null>(null);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const ultimosMeses = gerarUltimosMeses(6);

      const [
        { data: ugsData, error: ugsErr },
        { data: ucsData, error: ucsErr },
        { data: rateiosData, error: rateiosErr },
        { data: saldosData, error: saldosErr },
        { data: geracaoData, error: geracaoErr },
      ] = await Promise.all([
        supabase
          .from('rateio_unidades_geradoras')
          .select('*, distribuidora_config(nome, email_protocolo, dia_leitura_padrao)')
          .order('nome'),
        supabase
          .from('unidades_consumidoras')
          .select('id, numero_uc, outro_numero_uc, rateio_unidade_geradora_id, situacao')
          .not('rateio_unidade_geradora_id', 'is', null)
          .neq('situacao', 'Cancelada'),
        supabase
          .from('rateios')
          .select('id, rateio_unidade_geradora_id, mes_referencia, excedente_alocavel_kwh, ancora_kwh, status, updated_at')
          .order('updated_at', { ascending: false }),
        supabase.from('rateio_ancora_saldo_ug').select('saldo_ancora_kwh'),
        supabase.from('rateio_geracao_mensal').select('rateio_unidade_geradora_id, mes_referencia, geracao_kwh'),
      ]);
      if (ugsErr) throw ugsErr;
      if (ucsErr) throw ucsErr;
      if (rateiosErr) throw rateiosErr;
      if (saldosErr) throw saldosErr;
      if (geracaoErr) throw geracaoErr;

      const ugsList = (ugsData || []) as RateioUnidadeGeradora[];
      setUgs(ugsList);

      const ucs = (ucsData || []) as { id: number; numero_uc: string; outro_numero_uc: string | null; rateio_unidade_geradora_id: string }[];
      const rateiosTodos = (rateiosData || []) as any[]; // já ordenados por updated_at desc

      const rateioMaisRecentePorUg = new Map<string, any>();
      for (const r of rateiosTodos) {
        if (!rateioMaisRecentePorUg.has(r.rateio_unidade_geradora_id)) rateioMaisRecentePorUg.set(r.rateio_unidade_geradora_id, r);
      }

      const rateioCicloAtualPorUg = new Map<string, any>();
      for (const r of rateiosTodos) {
        if (normalizarMesReferencia(r.mes_referencia) !== mesSelecionado) continue;
        if (!rateioCicloAtualPorUg.has(r.rateio_unidade_geradora_id)) rateioCicloAtualPorUg.set(r.rateio_unidade_geradora_id, r);
      }
      const rateioIdsCicloAtual = Array.from(rateioCicloAtualPorUg.values()).map(r => r.id);

      const { data: itensData, error: itensErr } = rateioIdsCicloAtual.length
        ? await supabase.from('rateio_itens').select('rateio_id, unidade_consumidora_id, energia_alvo_kwh').in('rateio_id', rateioIdsCicloAtual)
        : { data: [] as any[], error: null };
      if (itensErr) throw itensErr;
      const itensCicloAtual = (itensData || []) as { rateio_id: string; unidade_consumidora_id: number; energia_alvo_kwh: number }[];

      const historicoPorUc = await buscarHistoricoConsumoUcs(ucs, { mesesLookback: 6 });

      const geracaoTotalKwh = round2(
        (geracaoData || [])
          .filter((g: any) => normalizarMesReferencia(g.mes_referencia) === mesSelecionado)
          .reduce((s: number, g: any) => s + (g.geracao_kwh || 0), 0)
      );

      const consumoAtendidoKwh = round2(itensCicloAtual.reduce((s, i) => s + (i.energia_alvo_kwh || 0), 0));

      const ucIdsCicloAtual = new Set(itensCicloAtual.map(i => i.unidade_consumidora_id));
      const consumoRealTotalKwh = Array.from(ucIdsCicloAtual).reduce(
        (s, ucId) => s + (historicoPorUc.get(ucId)?.[0]?.consumo_ativo_kwh || 0),
        0
      );
      const coberturaPercent = consumoRealTotalKwh > 0 ? round2((consumoAtendidoKwh / consumoRealTotalKwh) * 100) : null;

      const saldoAncoraTotalKwh = round2((saldosData || []).reduce((s: number, x: any) => s + (x.saldo_ancora_kwh || 0), 0));

      setKpis({ geracaoTotalKwh, consumoAtendidoKwh, coberturaPercent, saldoAncoraTotalKwh });

      const numUcsPorUg = new Map<string, number>();
      for (const uc of ucs) {
        numUcsPorUg.set(uc.rateio_unidade_geradora_id, (numUcsPorUg.get(uc.rateio_unidade_geradora_id) || 0) + 1);
      }

      setPorUg(
        ugsList.map(ug => {
          const r = rateioMaisRecentePorUg.get(ug.id);
          return {
            ug,
            numUcs: numUcsPorUg.get(ug.id) || 0,
            energiaDisponivelKwh: r ? round2(r.ancora_kwh) : null,
            percentDisponivel: r && r.excedente_alocavel_kwh > 0 ? round2((r.ancora_kwh / r.excedente_alocavel_kwh) * 100) : null,
          };
        })
      );

      const consumoPorMes = new Map<string, number>();
      for (const faturas of historicoPorUc.values()) {
        for (const f of faturas) consumoPorMes.set(f.mes_referencia, (consumoPorMes.get(f.mes_referencia) || 0) + (f.consumo_ativo_kwh || 0));
      }
      const geracaoPorMes = new Map<string, number>();
      for (const g of geracaoData || []) {
        const mes = normalizarMesReferencia((g as any).mes_referencia);
        geracaoPorMes.set(mes, (geracaoPorMes.get(mes) || 0) + ((g as any).geracao_kwh || 0));
      }
      setEvolucaoData(
        ultimosMeses.map(mes => ({
          mes: mes.split('/')[0],
          Consumo: round2(consumoPorMes.get(mes) || 0),
          Geração: round2(geracaoPorMes.get(mes) || 0),
        }))
      );

      const ucIdParaUgId = new Map(ucs.map(u => [u.id, u.rateio_unidade_geradora_id]));
      const sobraDeficitPorUg = new Map<string, { sobra: number; deficit: number }>();
      for (const item of itensCicloAtual) {
        const ugId = ucIdParaUgId.get(item.unidade_consumidora_id);
        if (!ugId) continue;
        const consumoReal = historicoPorUc.get(item.unidade_consumidora_id)?.[0]?.consumo_ativo_kwh;
        if (consumoReal == null) continue;
        const bucket = sobraDeficitPorUg.get(ugId) || { sobra: 0, deficit: 0 };
        if (item.energia_alvo_kwh > consumoReal) bucket.sobra += 1;
        else if (item.energia_alvo_kwh < consumoReal) bucket.deficit += 1;
        sobraDeficitPorUg.set(ugId, bucket);
      }
      setSobraDeficitData(
        ugsList
          .filter(ug => sobraDeficitPorUg.has(ug.id))
          .map(ug => ({ ug: ug.nome, Sobra: sobraDeficitPorUg.get(ug.id)!.sobra, Déficit: sobraDeficitPorUg.get(ug.id)!.deficit }))
      );
    } catch (error) {
      console.error('Error loading dashboard data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesSelecionado]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-gray-400" size={28} />
      </div>
    );
  }

  if (ugs.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-16 text-center">
        <Gauge className="mx-auto text-gray-300 dark:text-gray-600 mb-3" size={40} />
        <p className="text-gray-500 dark:text-gray-400">Nenhum rateio processado neste ciclo.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <select
          value={mesSelecionado}
          onChange={e => setMesSelecionado(e.target.value)}
          className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm"
        >
          {meses12.map(m => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={<Zap size={16} />} label="Geração Total Disponível" value={`${formatKwh(kpis.geracaoTotalKwh)} kWh`} />
        <KpiCard icon={<Gauge size={16} />} label="Consumo Total Atendido" value={`${formatKwh(kpis.consumoAtendidoKwh)} kWh`} />
        <KpiCard
          icon={<TrendingUp size={16} />}
          label="% Cobertura da Carteira"
          value={kpis.coberturaPercent != null ? `${kpis.coberturaPercent.toFixed(2)}%` : '—'}
        />
        <KpiCard icon={<Landmark size={16} />} label="Saldo Remanescente (Âncora)" value={`${formatKwh(kpis.saldoAncoraTotalKwh)} kWh`} />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-bold text-gray-800 dark:text-white">Por UG</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 px-4">UG</th>
                <th className="py-2 px-4">Distribuidora</th>
                <th className="py-2 px-4">Capacidade</th>
                <th className="py-2 px-4">UCs</th>
                <th className="py-2 px-4">% Disponível</th>
                <th className="py-2 px-4">Energia Disponível</th>
                <th className="py-2 px-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {porUg.map(({ ug, numUcs, energiaDisponivelKwh, percentDisponivel }) => (
                <tr key={ug.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 px-4 font-medium text-gray-900 dark:text-gray-100">{ug.nome}</td>
                  <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{ug.distribuidora_config?.nome || '—'}</td>
                  <td className="py-2 px-4 text-gray-600 dark:text-gray-300">
                    {ug.capacidade_kwp != null ? `${ug.capacidade_kwp.toLocaleString('pt-BR')} kWp` : '—'}
                  </td>
                  <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{numUcs}</td>
                  <td className="py-2 px-4 text-gray-600 dark:text-gray-300">{percentDisponivel != null ? `${percentDisponivel.toFixed(2)}%` : '—'}</td>
                  <td className="py-2 px-4 text-gray-600 dark:text-gray-300">
                    {energiaDisponivelKwh != null ? `${formatKwh(energiaDisponivelKwh)} kWh` : '—'}
                  </td>
                  <td className="py-2 px-4 text-right">
                    <button onClick={() => setShowRegistrarGeracao(ug)} className="text-sm text-primary hover:underline">
                      Registrar Geração
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Evolução Consumo vs. Geração">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={evolucaoData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" className="dark:opacity-10" />
              <XAxis dataKey="mes" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
              <RechartsTooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Consumo" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={16} />
              <Bar dataKey="Geração" fill="#5F6C37" radius={[4, 4, 0, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="UCs com Sobra vs. Déficit">
          {sobraDeficitData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-400">
              Sem UCs com fatura recente no ciclo selecionado.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sobraDeficitData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" className="dark:opacity-10" />
                <XAxis dataKey="ug" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} allowDecimals={false} />
                <RechartsTooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Sobra" fill="#5F6C37" radius={[4, 4, 0, 0]} barSize={16} />
                <Bar dataKey="Déficit" fill="#F6A937" radius={[4, 4, 0, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {showRegistrarGeracao && (
        <RegistrarGeracaoModal
          ug={showRegistrarGeracao}
          meses={meses12}
          mesInicial={mesSelecionado}
          onClose={() => setShowRegistrarGeracao(null)}
          onSalvo={() => {
            setShowRegistrarGeracao(null);
            carregarDados();
          }}
        />
      )}
    </div>
  );
};

export default DashboardRateioView;
